import type { QpFile } from "../gql/generated/schema"
import { getLocale } from "./locale.js"

export const demoFilters = ["quickpoint::caruso-italia", "quickpoint::nyt", "quickpoint::tufte"]

const demoPresentationEn = `title: Renewable Energy
subtitle: Powering a Sustainable Future

===
title: Solar Power
image: solar panels
  caption: Solar panels converting sunlight to electricity
The sun provides more energy in one hour than humanity uses in a year.

===
title: Wind Energy
image: wind turbines
  caption: Modern wind farm
Wind is now the cheapest source of new electricity in many markets, growing at 12% annually and creating thousands of local jobs.

===
title: The Future is Bright
subtitle: Together we can make a difference
`;

const demoPresentationFr = `title: Énergies Renouvelables
subtitle: Construire un avenir durable

===
title: Énergie Solaire
image: solar panels
  caption: Panneaux solaires convertissant la lumière du soleil en électricité
Le soleil fournit plus d'énergie en une heure que l'humanité n'en consomme en un an.

===
title: Énergie Éolienne
image: wind turbines
  caption: Parc éolien moderne
L'éolien est désormais la source d'électricité neuve la moins chère dans de nombreux marchés, avec une croissance de 12 % par an et des milliers d'emplois locaux créés.

===
title: L'avenir est radieux
subtitle: Ensemble, nous pouvons faire la différence
`;

const demoPresentations: Record<string, string> = {
	fr: demoPresentationFr,
};

/** @deprecated Use getDemoPresentationCode() for locale-aware content */
export const demoPresentationCode = demoPresentationEn;

export function getDemoPresentationCode(): string {
	const locale = getLocale();
	return demoPresentations[locale] ?? demoPresentationEn;
}

// a self-contained data: URI so the demo chart renders without any upload
const demoRainfallCsv = `Month,Rainfall (mm)
Jan,82
Feb,64
Mar,58
Apr,47
May,39
Jun,28
Jul,22
Aug,31
Sep,49
Oct,73
Nov,91
Dec,88`;

export const demoDataFiles: QpFile[] = [
    {
        altText: "Monthly rainfall in millimetres",
        url: `data:text/csv,${encodeURIComponent(demoRainfallCsv)}`,
        userDefinedName: "rainfall",
        cloudFlareName: "rainfall",
        fileName: "rainfall.csv",
        fileType: "text/csv",
        id: "rainfall",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    },
]

export const demoImages: QpFile[] = [
    {
        altText: "A Bauhaus 1923 Poster",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/de3ce162-51ac-4c98-776f-8b075fd84800/public",
        userDefinedName: "bauhaus1923",
        cloudFlareName: "1923",
        fileName: "1923",
        fileType: "image/png",
        id: "1923",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    },
    {
        altText: "Napoleons March",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/ac61daa1-5593-4da3-9e03-8bb57914a900/public",
        userDefinedName: "napoleonsMarch",
        cloudFlareName: "nep",
        fileName: "nep",
        fileType: "image/png",
        id: "nep",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    },
    {
        altText: "A Bauhaus Poster",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/f91397f8-b75d-4d48-bc2d-f113ebc7cd00/public",
        userDefinedName: "bauhaus colors",
        cloudFlareName: "poster",
        fileName: "poster",
        fileType: "image/png",
        id: "poster",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    },
    {
        altText: "A Bauhaus Music Poster",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/8697afae-9121-44c6-f6dd-839427dfec00/public",
        userDefinedName: "bauhausMusic",
        cloudFlareName: "music",
        fileName: "music",
        fileType: "image/png",
        id: "music",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    }, {
        userDefinedName: "bauhaus",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/90c5de0e-a543-44c1-36c0-390944fdf200/public",
        altText: "A poster from the Bahaus Movement",
        cloudFlareName: "baa",
        fileName: "baa",
        fileType: "image/png",
        id: "baa",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    }, {
        userDefinedName: "bubbles",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/21f70730-4978-4140-3b00-f6e973c47b00/public",
        altText: "White lines wiggling on a black background",
        cloudFlareName: "bubbs",
        fileName: "bubbs",
        fileType: "image/png",
        id: "bubbs",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    }, {
        userDefinedName: "subway",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/e4766ca2-30d8-4961-441b-646f625b4b00/public",
        altText: "An abstract subway poster",
        cloudFlareName: "subs",
        fileName: "subs",
        fileType: "image/png",
        id: "subs",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    }, {
        userDefinedName: "bauhaus interior",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/43c14c19-c01d-4d9d-0a2b-b7c736703600/public",
        altText: "A Bauhaus Interior",
        cloudFlareName: "bi",
        fileName: "bi",
        fileType: "image/png",
        id: "bi",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    },
    {
        altText: "Sigmund Freud",
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/f4e01183-0701-4ceb-9780-4b0a9ab57600/public",
        userDefinedName: "sigmund",
        cloudFlareName: "sigmund",
        fileName: "sigmund",
        fileType: "image/png",
        id: "sigmund",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0
    },
    {
        altText: "Wind turbines",
        cloudFlareName: "yDdrRbeCdm912Rh76VN9rZWdGx",
        fileName: "DJI_0280.jpg",
        fileType: "image/jpeg",
        id: "e36f46c1-f107-4869-34a9-6110c7c7cc00",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0,
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/e36f46c1-f107-4869-34a9-6110c7c7cc00/public",
        userDefinedName: "wind turbines"
    },
    {
        altText: "Solar Panels",
        cloudFlareName: "fodiTwHq-PAl1_pW0bgmp2AI0w",
        fileName: "solar-panels-roof-solar-cell-scaled.jpeg",
        fileType: "image/jpeg",
        id: "22b4fb4c-2c6a-443b-9b9d-8542f1d7ef00",
        ownerName: "demo",
        stackId: "demo",
        tags: [],
        uploadedOn: 0,
        url: "https://imagedelivery.net/KVUPIyykyRSaaOckEeUn5g/22b4fb4c-2c6a-443b-9b9d-8542f1d7ef00/public",
        userDefinedName: "solar panels"
    },
]




